/**
 * The crosswalk: which program standards each course content category counts toward.
 *
 * Authors tag a question with course content only. The program director maps
 * each content category to the standards it satisfies (NCLEX client needs,
 * CAPTE, outcomes) once, here. A question then counts toward every standard its
 * content categories are mapped to, so reports by standard are real without
 * asking anyone to tag a question twice. A few dozen rows replace thousands of
 * per-question tags.
 *
 * A link is "suggested" (Leo's proposal) or "confirmed" (the director's
 * decision). Only confirmed links count in reports; suggestions are shown for
 * review. The links are many to many.
 *
 * Reporting standards never roll up on their own: a question counts toward a
 * parent standard category only if someone mapped the parent. Accreditors read
 * these numbers, and every count has to trace to a decision.
 */

import { categoryWordRoot, categoryWords } from "./suggest"
import { getCategoryAncestors, getChildCategories } from "./tree"
import type { ExamCategory } from "./types"

export type CrosswalkStatus = "suggested" | "confirmed"

export type CrosswalkLink = {
  /** A course content category. */
  contentId: string
  /** A category in a program-level standard. */
  standardId: string
  status: CrosswalkStatus
  /** Who decided: "Leo" for a suggestion, the director's name once confirmed. */
  by: string
  at: string
}

export const linkKey = (link: Pick<CrosswalkLink, "contentId" | "standardId">) =>
  `${link.contentId}→${link.standardId}`

/** Word roots with a weight: a word in the category's own name says more than one in an ancestor's. */
function tokens(
  category: ExamCategory,
  categories: readonly ExamCategory[],
): Map<string, number> {
  const out = new Map<string, number>()
  const add = (text: string, weight: number) => {
    for (const word of categoryWords(text)) {
      const key = categoryWordRoot(word)
      out.set(key, Math.max(out.get(key) ?? 0, weight))
    }
  }
  add(category.name, 2)
  add(category.description ?? "", 1)
  for (const id of getCategoryAncestors([...categories], category.id)) {
    add(categories.find((row) => row.id === id)?.name ?? "", 1)
  }
  return out
}

/**
 * Leo's first pass: for every most-specific standard category, the content
 * categories whose names share its vocabulary. Proposals only.
 */
export function suggestCrosswalk(input: {
  content: readonly ExamCategory[]
  standards: readonly ExamCategory[]
  all: readonly ExamCategory[]
  perStandard?: number
  at?: string
}): CrosswalkLink[] {
  const perStandard = input.perStandard ?? 2
  const at = input.at ?? new Date().toISOString()
  const hasChildren = (id: string, rows: readonly ExamCategory[]) =>
    getChildCategories([...rows], id).length > 0
  const contentLeaves = input.content.filter((row) => !hasChildren(row.id, input.content))
  const contentTokens = new Map(
    contentLeaves.map((row) => [row.id, tokens(row, input.all)] as const),
  )
  const out: CrosswalkLink[] = []
  for (const standard of input.standards) {
    if (hasChildren(standard.id, input.standards)) continue
    const own = tokens(standard, input.all)
    const ranked = contentLeaves
      .map((row) => {
        let score = 0
        let ownNameHits = 0
        for (const [token, weight] of contentTokens.get(row.id) ?? []) {
          const other = own.get(token)
          if (!other) continue
          score += Math.min(weight, other)
          if (weight === 2 && other === 2) ownNameHits += 1
        }
        return { row, score, ownNameHits }
      })
      // A match must rest on a word both sides carry in their own names.
      .filter((entry) => entry.score >= 3 && entry.ownNameHits >= 1)
      .sort((a, b) => b.score - a.score)
      .slice(0, perStandard)
    for (const { row } of ranked) {
      out.push({ contentId: row.id, standardId: standard.id, status: "suggested", by: "Leo", at })
    }
  }
  return out
}

/**
 * The program standards a question counts toward, through its content
 * categories. A link on a parent category covers its children.
 */
export function standardsForQuestion(
  categoryIds: readonly string[],
  links: readonly CrosswalkLink[],
  all: readonly ExamCategory[],
  options: { includeSuggested?: boolean } = {},
): string[] {
  const reach = new Set(
    categoryIds.flatMap((id) => [id, ...getCategoryAncestors([...all], id)]),
  )
  const standardIds = links
    .filter(
      (link) =>
        reach.has(link.contentId) &&
        (link.status === "confirmed" || options.includeSuggested),
    )
    .map((link) => link.standardId)
  // Reporting standards are exact: a parent counts only if someone mapped it.
  return [...new Set(standardIds)]
}

/** The content categories that count toward one standard category. */
export function contentLinksForStandard(
  standardId: string,
  links: readonly CrosswalkLink[],
): CrosswalkLink[] {
  return links.filter((link) => link.standardId === standardId)
}

export function upsertLink(
  links: readonly CrosswalkLink[],
  next: CrosswalkLink,
): CrosswalkLink[] {
  const key = linkKey(next)
  return [...links.filter((link) => linkKey(link) !== key), next]
}

export function removeLink(
  links: readonly CrosswalkLink[],
  target: Pick<CrosswalkLink, "contentId" | "standardId">,
): CrosswalkLink[] {
  const key = linkKey(target)
  return links.filter((link) => linkKey(link) !== key)
}

/** Share of a bank's questions that count toward a standard category, by confirmed links. */
export function standardCoverage(
  standardId: string,
  questions: ReadonlyArray<{ categoryIds: readonly string[] }>,
  links: readonly CrosswalkLink[],
  all: readonly ExamCategory[],
): { count: number; total: number } {
  const count = questions.filter((question) =>
    standardsForQuestion(question.categoryIds, links, all).includes(standardId),
  ).length
  return { count, total: questions.length }
}

/**
 * How many questions count toward a whole standard, each counted once.
 *
 * A question mapped to three categories of the same tree is one question in the
 * tree's total, not three. (Each category still shows its own count.)
 */
export function treeCoverage(
  treeCategoryIds: readonly string[],
  questions: ReadonlyArray<{ categoryIds: readonly string[] }>,
  links: readonly CrosswalkLink[],
  all: readonly ExamCategory[],
): { count: number; total: number } {
  const tree = new Set(treeCategoryIds)
  const count = questions.filter((question) =>
    standardsForQuestion(question.categoryIds, links, all).some((id) => tree.has(id)),
  ).length
  return { count, total: questions.length }
}

/**
 * The director's decision about one question, on top of the mapping: standards
 * it counts toward although its content does not reach them, and standards it
 * does not count toward although its content does.
 */
export type StandardOverride = { add: string[]; remove: string[] }

export function applyStandardOverride(
  base: readonly string[],
  override: StandardOverride | undefined,
  _all?: readonly ExamCategory[],
): string[] {
  if (!override) return [...base]
  const removed = new Set(override.remove)
  return [...new Set([...base.filter((id) => !removed.has(id)), ...override.add])]
}

/** What the override must say for the question to end up counting toward exactly `chosen`. */
export function overrideFor(
  base: readonly string[],
  chosen: readonly string[],
): StandardOverride | undefined {
  const have = new Set(base)
  const want = new Set(chosen)
  const add = chosen.filter((id) => !have.has(id))
  const remove = base.filter((id) => !want.has(id))
  return add.length === 0 && remove.length === 0 ? undefined : { add, remove }
}

export type MappingStats = {
  /** Most specific categories in the standard: the ones content can satisfy. */
  leaves: number
  /** Leaves with at least one confirmed link. */
  mapped: number
  /** Leaves with only Leo's suggestions so far. */
  awaitingReview: number
  /** Leaves nothing points at. */
  gaps: number
}

/** How far a standard is mapped: the director's overview of one standard. */
export function mappingStats(
  standardCategories: readonly ExamCategory[],
  links: readonly CrosswalkLink[],
): MappingStats {
  const leaves = standardCategories.filter(
    (row) => getChildCategories([...standardCategories], row.id).length === 0,
  )
  let mapped = 0
  let awaitingReview = 0
  for (const leaf of leaves) {
    const own = links.filter((link) => link.standardId === leaf.id)
    if (own.some((link) => link.status === "confirmed")) mapped += 1
    else if (own.length > 0) awaitingReview += 1
  }
  return { leaves: leaves.length, mapped, awaitingReview, gaps: leaves.length - mapped - awaitingReview }
}
